import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionStatus } from '../context/SubscriptionContext';
import { UserMenu } from './UserMenu';
import { Shield, Code2, Users, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { GenerationIndicator } from './GenerationIndicator';
import { ladeUngelesen } from '../lib/anleitungenApi';

function PromptEditorToggle({ darkMode, userId }: { darkMode: boolean; userId: number }) {
  const key = `showPromptEditor_${userId}`;
  const [enabled, setEnabled] = useState(() => {
    try { const v = localStorage.getItem(key); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(key, String(next));
  };
  return (
    <button
      onClick={toggle}
      className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        enabled
          ? darkMode ? 'bg-amber-900/50 text-amber-400 hover:bg-amber-900/70' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
          : darkMode ? 'bg-slate-700 text-slate-500 hover:bg-slate-600' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
      }`}
      title={enabled ? 'Prompt-Editor aktiv (Klick zum Deaktivieren)' : 'Prompt-Editor inaktiv (Klick zum Aktivieren)'}
    >
      <Code2 className="w-3.5 h-3.5" />
      Prompts
    </button>
  );
}

/**
 * Anzahl noch nicht durchgesehener Anleitungen — speist die Plakette im Menü.
 *
 * Eigener kleiner Abruf statt Kontext: Er hängt an genau einem Menüpunkt und darf nicht
 * dafür sorgen, dass beim Ausfall der Anleitungen der ganze Kopf leer bleibt.
 */
function useUngeleseneAnleitungen(aktiv: boolean): number {
  const [anzahl, setAnzahl] = useState(0);
  const ort = useLocation();
  useEffect(() => {
    if (!aktiv) { setAnzahl(0); return; }
    let abgebrochen = false;
    ladeUngelesen()
      .then((d) => { if (!abgebrochen) setAnzahl(d.ungelesen); })
      .catch(() => { if (!abgebrochen) setAnzahl(0); });
    return () => { abgebrochen = true; };
    // Nach dem Besuch der Seite neu zählen — dort ändert sich der Stand.
  }, [aktiv, ort.pathname === '/anleitungen']);
  return anzahl;
}

const NAV_LINKS = [
  { label: 'Dashboard', path: '/' },
  { label: 'Kunden', path: '/customers' },
  { label: 'Berichte', path: '/reports' },
];

const PLAN_LABELS: Record<string, string> = {
  free: 'Free', pro: 'Pro', unlimited: 'Unlimited',
  starter: 'Starter', professional: 'Professional', business: 'Business', flat: 'Flat',
  partner: 'Partner',
};
function planBadgeClasses(plan: string, darkMode: boolean): string {
  if (plan === 'pro' || plan === 'professional') return darkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-100 text-blue-700 hover:bg-blue-200';
  if (plan === 'unlimited' || plan === 'flat') return darkMode ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60' : 'bg-purple-100 text-purple-700 hover:bg-purple-200';
  if (plan === 'starter') return darkMode ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200';
  if (plan === 'business') return darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200';
  if (plan === 'partner') return darkMode ? 'bg-amber-900/40 text-amber-300 hover:bg-amber-900/60' : 'bg-amber-100 text-amber-700 hover:bg-amber-200';
  return darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200';
}

function TeamBadge() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const teamName = (user as any)?.teamName;
  if (!teamName) return null;
  return (
    <button
      onClick={() => navigate('/settings#team')}
      title="Team-Einstellungen"
      className={`hidden md:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[13px] font-semibold transition-colors cursor-pointer ${
        darkMode ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
      }`}
    >
      <Users className="w-3 h-3" />
      {teamName}
    </button>
  );
}

function PlanBadge() {
  const { darkMode } = useDarkMode();
  const { status } = useSubscriptionStatus();
  const navigate = useNavigate();
  if (!status) return null;
  const plan = status.effectivePlan;
  const label = PLAN_LABELS[plan] ?? plan;
  const target = plan === 'free' ? '/preise' : '/settings#plan';
  const title = plan === 'free' ? 'Auf Pro upgraden' : 'Tarif verwalten';
  return (
    <button
      onClick={() => navigate(target)}
      title={title}
      className={`hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-semibold transition-colors cursor-pointer ${planBadgeClasses(plan, darkMode)}`}
    >
      {label}
    </button>
  );
}

export function Header() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const ungelesen = useUngeleseneAnleitungen(!!user?.isAdmin);
  const { status: subStatus } = useSubscriptionStatus();
  const templatesActive = !!subStatus?.features?.templates;

  // Mobiles Menü bei Routenwechsel schließen
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Editor öffnen — immer mit einem neuen, leeren Bericht. Bestehende Berichte öffnet man
  // gezielt über die Berichte-Liste (Nutzerwunsch 2026-10-28).
  const openEditor = () => navigate('/editor');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-30 border-b px-4 md:px-8 flex items-center justify-between ${
        darkMode
          ? 'bg-slate-800 border-slate-700'
          : 'bg-white border-slate-200'
      }`}
    >
      {/* Logo + Nav */}
      <div className="flex items-center gap-3 sm:gap-6">
        {/* Burger – nur auf kleinen Screens, nur eingeloggt (links) */}
        {user && (
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={mobileMenuOpen}
            className={`md:hidden flex items-center justify-center w-9 h-9 -ml-1 rounded-lg transition-colors cursor-pointer ${
              darkMode
                ? 'text-slate-300 hover:bg-slate-700'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
        {/* Logo */}
        <button
          onClick={() => navigate('/home')}
          className="flex items-center gap-2.5 py-3 cursor-pointer"
        >
          <Logo size="sm" />
        </button>

        {/* Navigation Links – nur wenn eingeloggt */}
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const isActive = link.path === '/'
                ? location.pathname === '/'
                : location.pathname === link.path || location.pathname.startsWith(link.path + '/');
              return (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`relative px-3 py-4 text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? darkMode
                        ? 'text-blue-400'
                        : 'text-blue-600'
                      : darkMode
                        ? 'text-slate-400 hover:text-slate-200'
                        : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </button>
              );
            })}
            {/* Editor — öffnet immer einen neuen, leeren Bericht */}
            {(() => {
              const isActive = location.pathname.startsWith('/editor');
              return (
                <button
                  onClick={openEditor}
                  className={`relative px-3 py-4 text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? darkMode ? 'text-blue-400' : 'text-blue-600'
                      : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Editor
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </button>
              );
            })()}
            {/* Vorlagen — nur sichtbar, wenn das Templates-Feature für den Account aktiv ist
                (templatesEnabled, siehe SubscriptionContext), konsistent mit der Sichtbarkeit
                von TemplateSelector/„Als Vorlage speichern" im Editor. */}
            {templatesActive && (() => {
              const isActive = location.pathname.startsWith('/templates');
              return (
                <button
                  onClick={() => navigate('/templates')}
                  className={`relative px-3 py-4 text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? darkMode ? 'text-blue-400' : 'text-blue-600'
                      : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Vorlagen
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </button>
              );
            })()}
            {/* Anleitungen — vorerst NUR für Admins sichtbar, solange die Inhalte fehlen.
                Die Plakette zeigt, wie viele Anleitungen noch niemand durchgesehen hat. */}
            {user?.isAdmin && (() => {
              const isActive = location.pathname.startsWith('/anleitungen');
              return (
                <button
                  onClick={() => navigate('/anleitungen')}
                  className={`relative px-3 py-4 text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? darkMode ? 'text-blue-400' : 'text-blue-600'
                      : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Anleitungen
                    {ungelesen > 0 && (
                      <span
                        title={`${ungelesen} noch nicht durchgesehen`}
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-amber-500 text-white"
                      >
                        {ungelesen}
                      </span>
                    )}
                  </span>
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </button>
              );
            })()}
          </nav>
        )}
      </div>

      {/* Rechte Seite */}
      <div className="flex items-center gap-2">
        {user && <GenerationIndicator />}
        {!user && (
          <Link
            to="/login"
            className="lg-btn px-4 py-1.5 text-sm font-medium"
          >
            Anmelden
          </Link>
        )}
        {user?.isAdmin && (
          <>
            <PromptEditorToggle darkMode={darkMode} userId={user.id} />
            <button
              onClick={() => navigate('/admin')}
              className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                location.pathname === '/admin'
                  ? 'bg-blue-600 text-white'
                  : darkMode
                    ? 'bg-slate-700 text-blue-400 hover:bg-slate-600'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
              title="Admin-Panel"
            >
              <Shield className="w-3.5 h-3.5" />
              Admin
            </button>
          </>
        )}
        <TeamBadge />
        <PlanBadge />
        <UserMenu />
      </div>

      {/* Mobiles Dropdown-Menü */}
      {user && mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-20 bg-black/30"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Panel */}
          <nav
            className={`md:hidden absolute top-full left-0 right-0 z-30 border-b shadow-lg flex flex-col py-2 ${
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            }`}
          >
            {NAV_LINKS.map((link) => {
              const isActive = link.path === '/'
                ? location.pathname === '/'
                : location.pathname === link.path || location.pathname.startsWith(link.path + '/');
              return (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`px-8 py-3 text-left text-base font-medium transition-colors cursor-pointer ${
                    isActive
                      ? darkMode ? 'text-blue-400 bg-slate-700/50' : 'text-blue-600 bg-blue-50'
                      : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </button>
              );
            })}
            {templatesActive && (
              <button
                onClick={() => navigate('/templates')}
                className={`px-8 py-3 text-left text-base font-medium transition-colors cursor-pointer ${
                  location.pathname.startsWith('/templates')
                    ? darkMode ? 'text-blue-400 bg-slate-700/50' : 'text-blue-600 bg-blue-50'
                    : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Vorlagen
              </button>
            )}
            {user.isAdmin && (
              <button
                onClick={() => navigate('/anleitungen')}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/anleitungen')
                    ? darkMode ? 'bg-slate-700 text-blue-400' : 'bg-slate-100 text-blue-600'
                    : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  Anleitungen
                  {ungelesen > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-amber-500 text-white">
                      {ungelesen}
                    </span>
                  )}
                </span>
              </button>
            )}

            {/* Editor */}
            <button
              onClick={openEditor}
              className={`px-8 py-3 text-left text-base font-medium transition-colors cursor-pointer ${
                location.pathname.startsWith('/editor')
                  ? darkMode ? 'text-blue-400 bg-slate-700/50' : 'text-blue-600 bg-blue-50'
                  : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              Editor
            </button>
            {/* Admin – nur für Admins */}
            {user.isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className={`flex items-center gap-2 px-8 py-3 text-left text-base font-medium transition-colors cursor-pointer ${
                  location.pathname === '/admin'
                    ? darkMode ? 'text-blue-400 bg-slate-700/50' : 'text-blue-600 bg-blue-50'
                    : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                Admin
              </button>
            )}
          </nav>
        </>
      )}
    </header>
  );
}
